import React, { useState } from 'react';
import { FaArrowLeft, FaSave, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Spinner, Form } from 'react-bootstrap';
import NavigationTree from './components/NavigationTree';
import AboutEditor from './components/AboutEditor';
import TipsEditor from './components/TipsEditor';
import OthersEditor from './components/OthersEditor';
import MobilePreview from './components/MobilePreview';
import LanguagePills from './components/LanguagePills';

const EditorWorkspace = ({
    profileContent,
    activeProfile,
    activeReportType,
    activeSection,
    activeLang,
    availableLang,
    availableLangObj,
    onChangeReportType,
    onChangeSection,
    onChangeLang,
    onChangeProfile,
    onBack,
    onUpdateContent,
    onSave,
    saveLoading
}) => {
    // Track which report types are expanded in the tree
    const [expandedTypes, setExpandedTypes] = useState({
        [activeReportType]: true
    });

    // Defensive access to profileContent
    const safeProfileContent = profileContent || {};
    const profiles = Object.keys(safeProfileContent).sort();

    // Safe index calculation with fallback
    let currentIndex = profiles.indexOf(activeProfile);

    // Use a ref to guard against infinite re-renders during corrections
    const correctionInProgress = React.useRef(false);

    // Handle invalid activeProfile state
    React.useEffect(() => {
        const currentIdx = profiles.indexOf(activeProfile);

        if (profiles.length === 0) {
            // No profiles available - clear state
            if (activeProfile !== null && !correctionInProgress.current) {
                correctionInProgress.current = true;
                onChangeProfile(null);
            }
        } else if (currentIdx === -1) {
            // Active profile not found in list - reset to first profile
            if (!correctionInProgress.current) {
                correctionInProgress.current = true;
                onChangeProfile(profiles[0]);
            }
        } else {
            // Valid state, reset guard
            correctionInProgress.current = false;
        }
    }, [profiles, activeProfile, onChangeProfile]);

    // Shared navigation helper for consistent behavior
    const navigateToProfile = (profile) => {
        onChangeProfile(profile);
        setExpandedTypes({ advanced: true });
        onChangeReportType('advanced');
        onChangeSection('about');
    };

    // Toggle expand/collapse for a report type
    const handleToggleExpand = (reportTypeId) => {
        setExpandedTypes(prev => ({
            ...prev,
            [reportTypeId]: !prev[reportTypeId]
        }));
    };

    // Navigate to next/prev profile using shared helper
    const handlePrevProfile = () => {
        if (currentIndex > 0) {
            navigateToProfile(profiles[currentIndex - 1]);
        }
    };

    const handleNextProfile = () => {
        if (currentIndex < profiles.length - 1) {
            navigateToProfile(profiles[currentIndex + 1]);
        }
    };

    // Get current section data with defensive access
    const profileData = safeProfileContent[activeProfile] || {};
    const reportTypeData = profileData[activeReportType] || {};
    const sectionData = activeSection === 'others'
        ? reportTypeData
        : (reportTypeData[activeSection] || {});

    // Generic update handler
    const handleDataUpdate = (sectionKey, newData) => {
        const updatedContent = {
            ...safeProfileContent,
            [activeProfile]: {
                ...safeProfileContent[activeProfile],
                [activeReportType]: {
                    ...safeProfileContent[activeProfile]?.[activeReportType],
                    [sectionKey]: newData
                }
            }
        };
        onUpdateContent(updatedContent);
    };

    // Render the appropriate editor based on active section
    const renderEditor = () => {
        const commonProps = {
            activeLang,
            availableLang,
            availableLangObj
        };

        switch (activeSection) {
            case 'about':
                return (
                    <AboutEditor
                        data={reportTypeData.about || {}}
                        reportType={activeReportType}
                        onUpdate={(newData) => handleDataUpdate('about', newData)}
                        {...commonProps}
                    />
                );
            case 'tips':
                return (
                    <TipsEditor
                        data={reportTypeData.tips || []}
                        onUpdate={(newData) => handleDataUpdate('tips', newData)}
                        {...commonProps}
                    />
                );
            case 'others':
                return (
                    <OthersEditor
                        data={reportTypeData}
                        profileContent={profileContent}
                        activeProfile={activeProfile}
                        activeReportType={activeReportType}
                        onUpdateContent={onUpdateContent}
                    />
                );
            default:
                return <div className="pfc-placeholder">Select a section to edit.</div>;
        }
    };

    return (
        <div className="pfc-editor-workspace">
            {/* Top Header Bar */}
            <header className="pfc-editor-header">
                <div className="pfc-editor-header-left">
                    <button className="pfc-btn pfc-btn-ghost" onClick={onBack}>
                        <FaArrowLeft /> Back
                    </button>
                    <div className="pfc-profile-nav">
                        <button
                            className="pfc-nav-arrow"
                            onClick={handlePrevProfile}
                            disabled={currentIndex <= 0}
                            aria-label="Previous profile"
                        >
                            <FaChevronLeft />
                        </button>
                        <Form.Select
                            className="pfc-profile-selector"
                            value={activeProfile}
                            onChange={(e) => {
                                navigateToProfile(e.target.value);
                            }}
                        >
                            {profiles.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </Form.Select>
                        <button
                            className="pfc-nav-arrow"
                            onClick={handleNextProfile}
                            disabled={currentIndex >= profiles.length - 1}
                            aria-label="Next profile"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
                <div className="pfc-editor-header-right">
                    <button
                        className="pfc-btn pfc-btn-primary"
                        onClick={onSave}
                        disabled={saveLoading}
                    >
                        {saveLoading ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <>
                                <FaSave /> Save Changes
                            </>
                        )}
                    </button>
                </div>
            </header>

            {/* Main Content Area - Three Panel Layout */}
            <div className="pfc-editor-body">
                {/* Left Panel - Navigation Tree */}
                <aside className="pfc-editor-sidebar">
                    <NavigationTree
                        profileContent={profileContent}
                        activeProfile={activeProfile}
                        activeReportType={activeReportType}
                        activeSection={activeSection}
                        onChangeReportType={onChangeReportType}
                        onChangeSection={onChangeSection}
                        expandedTypes={expandedTypes}
                        onToggleExpand={handleToggleExpand}
                    />
                </aside>

                {/* Center Panel - Editor */}
                <main className="pfc-editor-main">
                    {/* Language Pills */}
                    <LanguagePills
                        activeLang={activeLang}
                        availableLang={availableLang}
                        onChangeLang={onChangeLang}
                    />

                    {/* Section Editor */}
                    <div className="pfc-editor-content">
                        {renderEditor()}
                    </div>
                </main>

                {/* Right Panel - Preview */}
                <aside className="pfc-editor-preview">
                    <MobilePreview
                        profileContent={profileContent}
                        activeProfile={activeProfile}
                        activeReportType={activeReportType}
                        activeSection={activeSection}
                        activeLang={activeLang}
                    />
                </aside>
            </div>
        </div>
    );
};

export default EditorWorkspace;
